import { inventoryItemIdFormat } from "@redotech/redo-ims-id";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import { ProcessingActionType } from "@redotech/redo-model/return-flow/processing-action";
import { sortingServiceLevel } from "@redotech/redo-model/returns/sorting-service-level";
import { productFamilyIdFormat } from "@redotech/redo-product-id";
import { ReturnStatusZod } from "@redotech/redo-return-core-common/return-status-zod";
import { GradeAcceptance } from "@redotech/redo-return-settings-common/grade-mapping";
import { ReturnProcessingLevel } from "@redotech/redo-return-settings-common/return-processing-level";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

export const ReturnProcessingStepScope = {
  RETURN: "return",
  RETURN_ITEM: "return_item",
} as const;
export type ReturnProcessingStepScope =
  (typeof ReturnProcessingStepScope)[keyof typeof ReturnProcessingStepScope];
const returnProcessingStepScopeValues = Object.values(
  ReturnProcessingStepScope,
) as [ReturnProcessingStepScope, ...ReturnProcessingStepScope[]];

export const ReturnProcessingStepType = {
  DECISION: "return_processing_decision",
  INFORMATION: "return_processing_information",
  LICENSE_PLATE: "license_plate",
  SORT: "sort",
  EVIDENCE: "evidence",
  ADD_UNDECLARED_ITEM: "add_undeclared_item",
  VERIFICATION: "verification",
  PROCESSING_ACTION: "processing_action",
  VERIFY_ITEM_VARIANT: "verify_item_variant",
} as const;
export type ReturnProcessingStepType =
  (typeof ReturnProcessingStepType)[keyof typeof ReturnProcessingStepType];

const ReturnProcessingDecisionStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("return_processing_decision"),
  prompt: z.string(),
  decisionKey: z.string().optional(),
  trueLabel: z.string(),
  falseLabel: z.string(),
  nextTrueId: z.number().nullable(),
  nextFalseId: z.number().nullable(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
  imageUrl: z.string().optional(),
});
export const ReturnProcessingDecisionStep: z.ZodType<ReturnProcessingDecisionStep> =
  ReturnProcessingDecisionStep_;
export interface ReturnProcessingDecisionStep extends z.infer<
  typeof ReturnProcessingDecisionStep_
> {}

const ReturnProcessingInformationStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("return_processing_information"),
  header: z.string(),
  message: z.string(),
  continueButtonText: z.string(),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
  imageUrl: z.string().optional(),
});
export const ReturnProcessingInformationStep: z.ZodType<ReturnProcessingInformationStep> =
  ReturnProcessingInformationStep_;
export interface ReturnProcessingInformationStep extends z.infer<
  typeof ReturnProcessingInformationStep_
> {}

const LicensePlateStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("license_plate"),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const LicensePlateStep: z.ZodType<LicensePlateStep> = LicensePlateStep_;
export interface LicensePlateStep extends z.infer<typeof LicensePlateStep_> {}

const SortStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("sort"),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  destination: z.string(),
  customTitle: z.string().optional(),
});
export const SortStep: z.ZodType<SortStep> = SortStep_;
export interface SortStep extends z.infer<typeof SortStep_> {}

const EvidenceStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("evidence"),
  prompt: z.string(),
  notesLabel: z.string().optional(),
  notesPlaceholder: z.string().optional(),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
  imageUrl: z.string().optional(),
});
export const EvidenceStep: z.ZodType<EvidenceStep> = EvidenceStep_;
export interface EvidenceStep extends z.infer<typeof EvidenceStep_> {}

const AddUndeclaredItemStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("add_undeclared_item"),
  prompt: z.string(),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const AddUndeclaredItemStep: z.ZodType<AddUndeclaredItemStep> =
  AddUndeclaredItemStep_;
export interface AddUndeclaredItemStep extends z.infer<
  typeof AddUndeclaredItemStep_
> {}

const VerificationStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("verification"),
  prompt: z.string(),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const VerificationStep: z.ZodType<VerificationStep> = VerificationStep_;
export interface VerificationStep extends z.infer<typeof VerificationStep_> {}

const AssignGradeAction_ = z.object({
  type: z.literal(ProcessingActionType.AssignGrade),
  grade: z.string().min(1),
});

const ProcessingAction_ = z.discriminatedUnion("type", [AssignGradeAction_]);

const ProcessingActionStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal(ReturnProcessingStepType.PROCESSING_ACTION),
  action: ProcessingAction_,
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const ProcessingActionStep: z.ZodType<ProcessingActionStep> =
  ProcessingActionStep_;
export interface ProcessingActionStep extends z.infer<
  typeof ProcessingActionStep_
> {}

const VerifyItemVariantStep_ = z.object({
  id: zExt.objectId().optional(),
  type: z.literal("verify_item_variant"),
  prompt: z.string(),
  matchLabel: z.string(),
  mismatchLabel: z.string(),
  next: z.number().nullable().optional(),
  scope: z.enum(returnProcessingStepScopeValues),
  customTitle: z.string().optional(),
});
export const VerifyItemVariantStep: z.ZodType<VerifyItemVariantStep> =
  VerifyItemVariantStep_;
export interface VerifyItemVariantStep extends z.infer<
  typeof VerifyItemVariantStep_
> {}

const ReturnProcessingStep_ = z.discriminatedUnion("type", [
  ReturnProcessingDecisionStep_,
  ReturnProcessingInformationStep_,
  LicensePlateStep_,
  SortStep_,
  EvidenceStep_,
  AddUndeclaredItemStep_,
  VerificationStep_,
  ProcessingActionStep_,
  VerifyItemVariantStep_,
]);
export const ReturnProcessingStep: z.ZodType<ReturnProcessingStep> =
  ReturnProcessingStep_;
export type ReturnProcessingStep = z.infer<typeof ReturnProcessingStep_>;

const ReturnProcessingFlow_ = z.object({
  steps: z.array(ReturnProcessingStep_),
});
export const ReturnProcessingFlow: z.ZodType<ReturnProcessingFlow> =
  ReturnProcessingFlow_;
export interface ReturnProcessingFlow extends z.infer<
  typeof ReturnProcessingFlow_
> {}

const ProcessingReturnItem_ = z.object({
  id: zExt.objectId(),
  productTitle: z.string(),
  variantTitle: z.string(),
  sku: z.string().nullish(),
  barcode: z.string().nullish(),
  quantity: z.number().int().positive(),
  images: z.array(z.string().nullable()),
  reason: z.string().nullish(),
  price: z.string(),
  currencyCode: z.string().nullish(),
  vendor: z.string().nullish(),
  description: z.string().nullish(),
  customerNotes: z.string().nullish(),
  customerImages: z.array(z.string()),
  productFamilyId: productFamilyIdFormat.schema.nullish(),
  productFamilyTitle: z.string().nullish(),
  productFamilyVariantCount: z.number().int().nullish(),
});
export const ProcessingReturnItem: z.ZodType<ProcessingReturnItem> =
  ProcessingReturnItem_;
export interface ProcessingReturnItem extends z.infer<
  typeof ProcessingReturnItem_
> {}

const DispositionSettings_ = z.object({
  enabled: z.boolean(),
  gradeOptions: z.array(z.string()),
  outcomeOptions: z.array(z.string()),
});
export const DispositionSettings: z.ZodType<DispositionSettings> =
  DispositionSettings_;
export interface DispositionSettings extends z.infer<
  typeof DispositionSettings_
> {}

const GradeMappingSchema_ = z.object({
  gradeName: z.string(),
  acceptance: z.enum([
    GradeAcceptance.ACCEPT,
    GradeAcceptance.REJECT,
    GradeAcceptance.FLAG_FOR_REVIEW,
  ]),
  restock: z.boolean(),
});
export const GradeMappingSchema: z.ZodType<GradeMappingSchema> =
  GradeMappingSchema_;
export type GradeMappingSchema = z.infer<typeof GradeMappingSchema_>;

const SortingDestination_ = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("located"),
    locationName: z.string(),
    isTote: z.boolean(),
  }),
  z.object({
    mode: z.literal("needs_placement"),
    groupName: z.string(),
    toteFormatExample: z.string(),
    toteNames: z.array(z.string()),
  }),
]);
export const SortingDestination: z.ZodType<SortingDestination> =
  SortingDestination_;
export type SortingDestination = z.infer<typeof SortingDestination_>;

const ProcessingReturnData_ = z.object({
  id: zExt.objectId(),
  orderNumber: z.string(),
  status: ReturnStatusZod,
  items: z.array(ProcessingReturnItem_),
  dispositionSettings: DispositionSettings_.nullable(),
  gradeMappings: z.array(GradeMappingSchema_),
  returnProcessingFlow: ReturnProcessingFlow_,
  serviceLevelDestination: z.string(),
  serviceLevelSort: z.enum(sortingServiceLevel),
  sortingDestination: SortingDestination_.nullable(),
  createdAt: z.date(),
  inventoryItemId: inventoryItemIdFormat.schema.nullable(),
  processingAgentEvalEnabled: z.boolean(),
  returnProcessingLevel: z.enum(ReturnProcessingLevel).nullable(),
  wmsSourceOfTruthEnabled: z.boolean(),
  isFirstManagedReturn: z.boolean(),
  merchantName: z.string().nullable(),
});
export const ProcessingReturnData: z.ZodType<ProcessingReturnData> =
  ProcessingReturnData_;
export interface ProcessingReturnData extends z.infer<
  typeof ProcessingReturnData_
> {}

export const getReturnForProcessing = {
  name: "getReturnForProcessing" as const,
  input: z.object({
    identifier: z.string().min(1),
    locationId: locationIdFormat.schema.optional(),
  }),
  output: z.object({ return: ProcessingReturnData_.nullable() }),
};
